import React from 'react'
import {
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Typography,
  Box,
  Chip,
  IconButton,
  Button,
  Stack,
  Divider,
  Tooltip,
  Avatar
} from '@mui/material'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  AttachMoney as PriceIcon,
  CalendarToday as DateIcon,
  LocationOn as LocationIcon,
  Inventory as StatusIcon,
  Inventory2 as QuantityIcon,
  MoveDown as MoveIcon,
  SmokingRooms as SessionIcon
} from '@mui/icons-material'
import { Cigar } from '@store/api/cigarApi'
import { formatCigarTitle, formatHumidorLabel } from '@utils/cigarDisplay'
import { TOUCH_TARGET_MIN_PX } from '@components/layout/layoutConstants'
import { format } from 'date-fns'

interface CigarCardProps {
  cigar: Cigar
  onEdit?: (cigar: Cigar) => void
  onDelete?: (cigarId: string) => void
  onMove?: (cigar: Cigar) => void
  onView?: (cigar: Cigar) => void
  onStartSession?: (cigar: Cigar) => void
  showActions?: boolean
  viewMode?: 'grid' | 'list'
  selectionMode?: boolean
  selected?: boolean
  onToggleSelect?: (cigarId: string) => void
}

const CigarCard: React.FC<CigarCardProps> = ({
  cigar,
  onEdit,
  onDelete,
  onMove,
  onView,
  onStartSession,
  showActions = true,
  viewMode = 'grid',
  selectionMode = false,
  selected = false,
  onToggleSelect,
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'success'
      case 'smoked': return 'default'
      case 'traded': return 'info'
      case 'gifted': return 'warning'
      case 'damaged': return 'error'
      default: return 'default'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'available': return 'Available'
      case 'smoked': return 'Smoked'
      case 'traded': return 'Traded'
      case 'gifted': return 'Gifted'
      case 'damaged': return 'Damaged'
      default: return status
    }
  }

  const formatPrice = (price?: number | string) => {
    if (!price) return 'N/A'
    const numPrice = typeof price === 'string' ? parseFloat(price) : price
    if (isNaN(numPrice)) return 'N/A'
    return `$${numPrice.toFixed(2)}`
  }

  const formatDate = (date?: string) => {
    if (!date) return 'N/A'
    return format(new Date(date), 'MMM dd, yyyy')
  }

  const getCigarImage = () => {
    if (cigar.imageUrls && cigar.imageUrls.length > 0) {
      return cigar.imageUrls[0]
    }
    // Generate a simple SVG placeholder with the brand initial
    const initial = cigar.brand.name.charAt(0).toUpperCase()
    return `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="300" height="200"><rect width="300" height="200" fill="%23795548"/><text x="150" y="110" font-family="Arial" font-size="64" fill="white" text-anchor="middle" dominant-baseline="middle">${initial}</text></svg>`)}`
  }

  if (viewMode === 'list') {
    return (
      <Card
        data-testid="cigar-card-list"
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          mb: 2,
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <CardMedia
          component="img"
          sx={{
            width: { xs: '100%', sm: 140 },
            height: { xs: 160, sm: 140 },
            objectFit: 'cover',
            flexShrink: 0,
          }}
          image={getCigarImage()}
          alt={formatCigarTitle(cigar.brand.name, cigar.cigarLine.name)}
        />
        <CardContent sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', py: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} gap={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" component="h3" sx={{ wordBreak: 'break-word' }}>
                {formatCigarTitle(cigar.brand.name, cigar.cigarLine.name)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {cigar.vitola.name} • {cigar.length}&quot; × {cigar.ringGauge} RG
              </Typography>
            </Box>
            <Chip
              label={getStatusLabel(cigar.status)}
              color={getStatusColor(cigar.status) as any}
              size="small"
            />
          </Box>

          <Stack direction="row" spacing={2} mb={1} flexWrap="wrap" useFlexGap>
            {cigar.quantity > 1 && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <QuantityIcon fontSize="small" color="action" />
                <Typography variant="body2">Qty: {cigar.quantity}</Typography>
              </Box>
            )}
            {cigar.purchasePrice && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <PriceIcon fontSize="small" color="action" />
                <Typography variant="body2">{formatPrice(cigar.purchasePrice)}</Typography>
              </Box>
            )}
            {cigar.purchaseDate && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <DateIcon fontSize="small" color="action" />
                <Typography variant="body2">{formatDate(cigar.purchaseDate)}</Typography>
              </Box>
            )}
            {cigar.purchaseLocation && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <LocationIcon fontSize="small" color="action" />
                <Typography variant="body2">{cigar.purchaseLocation}</Typography>
              </Box>
            )}
          </Stack>

          <Typography variant="body2" color="text.secondary">
            In: {formatHumidorLabel(cigar.humidor.name)}
          </Typography>

          {cigar.notes && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
              &ldquo;{cigar.notes}&rdquo;
            </Typography>
          )}
        </CardContent>

        {selectionMode && (
          <CardActions sx={{ px: 2, pb: 2, alignItems: 'stretch' }}>
            <Button
              variant={selected ? 'contained' : 'outlined'}
              onClick={() => onToggleSelect?.(cigar.id)}
              aria-pressed={selected}
              sx={{ minHeight: TOUCH_TARGET_MIN_PX, minWidth: TOUCH_TARGET_MIN_PX, width: '100%' }}
            >
              {selected ? 'Selected' : 'Select'}
            </Button>
          </CardActions>
        )}

        {showActions && !selectionMode && (
          <CardActions sx={{ flexDirection: 'column', alignItems: 'stretch', px: 2, pb: 2 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {onStartSession && cigar.status === 'available' && (
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<SessionIcon />}
                  onClick={() => onStartSession(cigar)}
                  sx={{ minHeight: TOUCH_TARGET_MIN_PX, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
                >
                  Smoke
                </Button>
              )}
              {onView && (
                <Tooltip title="View Details">
                  <IconButton size="small" onClick={() => onView(cigar)} aria-label="View Details">
                    <ViewIcon />
                  </IconButton>
                </Tooltip>
              )}
              {onMove && (
                <Tooltip title="Move to Humidor">
                  <IconButton size="small" onClick={() => onMove(cigar)} color="secondary" aria-label="Move to Humidor">
                    <MoveIcon />
                  </IconButton>
                </Tooltip>
              )}
              {onEdit && (
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => onEdit(cigar)} color="primary" aria-label="Edit">
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
              {onDelete && (
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => onDelete(cigar.id)} color="error" aria-label="Delete">
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </CardActions>
        )}
      </Card>
    )
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardMedia
        component="img"
        height="200"
        image={getCigarImage()}
        alt={formatCigarTitle(cigar.brand.name, cigar.cigarLine.name)}
        sx={{ objectFit: 'cover' }}
      />
      <CardContent sx={{ flexGrow: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="h6" component="h3" noWrap>
            {cigar.brand.name}
          </Typography>
          <Chip
            label={getStatusLabel(cigar.status)}
            color={getStatusColor(cigar.status) as any}
            size="small"
          />
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          {cigar.cigarLine.name}
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          {cigar.vitola.name} • {cigar.length}&quot; × {cigar.ringGauge} RG
        </Typography>

        <Divider sx={{ my: 1 }} />

        <Stack spacing={1}>
          {cigar.quantity > 1 && (
            <Box display="flex" alignItems="center" gap={1}>
              <QuantityIcon fontSize="small" color="action" />
              <Typography variant="body2">Qty: {cigar.quantity}</Typography>
            </Box>
          )}
          {cigar.purchasePrice && (
            <Box display="flex" alignItems="center" gap={1}>
              <PriceIcon fontSize="small" color="action" />
              <Typography variant="body2">{formatPrice(cigar.purchasePrice)}</Typography>
            </Box>
          )}

          {cigar.purchaseDate && (
            <Box display="flex" alignItems="center" gap={1}>
              <DateIcon fontSize="small" color="action" />
              <Typography variant="body2">{formatDate(cigar.purchaseDate)}</Typography>
            </Box>
          )}

          <Box display="flex" alignItems="center" gap={1}>
            <StatusIcon fontSize="small" color="action" />
            <Typography variant="body2">In: {formatHumidorLabel(cigar.humidor.name)}</Typography>
          </Box>

          {cigar.purchaseLocation && (
            <Box display="flex" alignItems="center" gap={1}>
              <LocationIcon fontSize="small" color="action" />
              <Typography variant="body2" noWrap>{cigar.purchaseLocation}</Typography>
            </Box>
          )}
        </Stack>

        {cigar.notes && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              &ldquo;{cigar.notes}&rdquo;
            </Typography>
          </>
        )}

        {cigar.batchNumber && (
          <Box mt={1}>
            <Chip
              label={`Batch: ${cigar.batchNumber}`}
              size="small"
              variant="outlined"
            />
          </Box>
        )}

        {cigar.imageUrls && cigar.imageUrls.length > 1 && (
          <Box mt={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {cigar.imageUrls.slice(1, 4).map((url, index) => (
                <Avatar
                  key={index}
                  src={url}
                  variant="rounded"
                  sx={{ width: 32, height: 32 }}
                />
              ))}
              {cigar.imageUrls.length > 4 && (
                <Avatar
                  variant="rounded"
                  sx={{ width: 32, height: 32, bgcolor: 'grey.300' }}
                >
                  <Typography variant="caption">+{cigar.imageUrls.length - 4}</Typography>
                </Avatar>
              )}
            </Stack>
          </Box>
        )}
      </CardContent>

      {showActions && (
        <CardActions>
          <Box display="flex" gap={1} ml="auto">
            {onStartSession && cigar.status === 'available' && (
              <Tooltip title="Start Session">
                <IconButton size="small" onClick={() => onStartSession(cigar)} color="success">
                  <SessionIcon />
                </IconButton>
              </Tooltip>
            )}
            {onView && (
              <Tooltip title="View Details">
                <IconButton size="small" onClick={() => onView(cigar)}>
                  <ViewIcon />
                </IconButton>
              </Tooltip>
            )}
            {onMove && (
              <Tooltip title="Move to Humidor">
                <IconButton size="small" onClick={() => onMove(cigar)} color="secondary">
                  <MoveIcon />
                </IconButton>
              </Tooltip>
            )}
            {onEdit && (
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => onEdit(cigar)} color="primary">
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => onDelete(cigar.id)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </CardActions>
      )}
    </Card>
  )
}

export default CigarCard
